import { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Search, ChevronDown } from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/datetime';

function SearchSelect({ options, value, onChange, placeholder, searchPlaceholder, getLabel, getSearchLabel }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const ref = useRef(null);

    const filtered = query
        ? options.filter(o => (getSearchLabel ? getSearchLabel(o) : getLabel(o)).toLowerCase().includes(query.toLowerCase()))
        : options;

    const selected = options.find(o => o.id === value);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} className="relative">
            <div
                className="flex h-9 w-full items-center justify-between rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-1 text-sm text-white cursor-pointer"
                onClick={() => setOpen(!open)}
            >
                <span className={selected ? '' : 'text-white/40'}>{selected ? getLabel(selected) : placeholder}</span>
                <ChevronDown className="h-4 w-4 text-white/60" />
            </div>
            {open && (
                <div className="absolute z-50 mt-1 w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] shadow-lg">
                    <div className="p-1">
                        <input
                            className="w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-2 py-1.5 text-sm text-white placeholder:text-white/40 outline-none"
                            placeholder={searchPlaceholder || 'Buscar...'}
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                        {filtered.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-white/40">Sin resultados</div>
                        ) : (
                            filtered.map(o => (
                                <div
                                    key={o.id}
                                    className={`px-3 py-2 text-sm cursor-pointer ${value === o.id ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/5'}`}
                                    onClick={() => { onChange(o.id); setOpen(false); setQuery(''); }}
                                >
                                    {getSearchLabel ? getSearchLabel(o) : getLabel(o)}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function OwnerCards() {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [plans, setPlans] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [form, setForm] = useState({ customer_id: '', start_date: '', total_classes: '', gym_plan_id: '', payment_method: '' });
    const [editForm, setEditForm] = useState({ total_classes: '', used_classes: '', status: '', end_date: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const fetchCards = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/owner/cards', { params: { search, page, per_page: 10 } });
            setCards(res.data.data);
            setMeta({ last_page: res.data.last_page, current_page: res.data.current_page, total: res.data.total });
        } catch {} finally { setLoading(false); }
    }, [search, page]);

    useEffect(() => { fetchCards(); }, [fetchCards]);

    const openCreate = async () => {
        setEditing(null);
        setForm({ customer_id: '', start_date: new Date().toISOString().split('T')[0], total_classes: '', gym_plan_id: '', payment_method: '' });
        setError('');
        try {
            const [custRes, planRes, pmRes] = await Promise.all([
                api.get('/owner/customers?per_page=200'),
                api.get('/owner/gym-plans'),
                api.get('/owner/gyms/' + (localStorage.getItem('selected_gym_id') || '') + '/payment-methods'),
            ]);
            setCustomers(custRes.data.data);
            setPlans(planRes.data.data || []);
            setPaymentMethods(pmRes.data.data || []);
        } catch {}
        setDialogOpen(true);
    };

    const openEdit = (card) => {
        setEditing(card);
        setEditForm({
            total_classes: card.total_classes || '',
            used_classes: card.used_classes || 0,
            status: card.status || 'active',
            end_date: card.end_date ? card.end_date.split('T')[0] : '',
        });
        setError('');
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (editing) {
            const changes = [];
            if (editForm.end_date !== editing.end_date?.split('T')[0]) {
                changes.push(`Fecha de fin: ${formatDate(editing.end_date)} → ${formatDate(editForm.end_date)}`);
            }
            if (String(editForm.total_classes) !== String(editing.total_classes)) {
                changes.push(`Total de clases: ${editing.total_classes} → ${editForm.total_classes}`);
            }
            if (String(editForm.used_classes) !== String(editing.used_classes)) {
                changes.push(`Clases usadas: ${editing.used_classes} → ${editForm.used_classes}`);
            }
            if (editForm.status !== editing.status) {
                changes.push(`Estado: ${editing.status} → ${editForm.status}`);
            }
            const confirmMessage = changes.length
                ? `¿Estás seguro de guardar los siguientes cambios en la tarjeta?\n\n${changes.join('\n')}`
                : '¿Estás seguro de guardar los cambios en la tarjeta?';
            if (!confirm(confirmMessage)) return;
        }
        setSaving(true);
        setError('');
        try {
            if (editing) {
                await api.put(`/owner/cards/${editing.id}`, editForm);
            } else {
                await api.post('/owner/cards', {
                    customer_id: form.customer_id,
                    start_date: form.start_date,
                    total_classes: parseInt(form.total_classes),
                    gym_plan_id: form.gym_plan_id || null,
                    payment_method: form.payment_method || null,
                });
            }
            setDialogOpen(false);
            fetchCards();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar.');
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar esta tarjeta?')) return;
        try {
            await api.delete(`/owner/cards/${id}`);
            fetchCards();
        } catch {}
    };

    const isDateBeforeToday = (dateStr) => {
        if (!dateStr) return false;
        const date = new Date(`${dateStr.split('T')[0]}T00:00:00`);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    };

    const getCardEstado = (card) => {
        if (card.status === 'cancelled') return 'Cancelada';
        const maxClassesReached = Number(card.total_classes) > 0 && Number(card.used_classes) >= Number(card.total_classes);
        const expired = card.status === 'expired' || isDateBeforeToday(card.end_date);
        return expired || maxClassesReached ? 'Inactiva' : 'Activa';
    };

    const statusBadge = (card) => {
        const estado = getCardEstado(card);
        const isActive = estado === 'Activa';
        return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isActive ? 'bg-white text-black' : 'bg-white/10 text-white'}`}>
                {estado}
            </span>
        );
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Tarjetas</h1>
                <Button onClick={openCreate} className="w-full sm:w-auto"><Plus className="h-4 w-4 mr-2" />Nueva Tarjeta</Button>
            </div>

            <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                <CardHeader>
                    <div className="flex flex-wrap items-center gap-2">
                        <Search className="h-4 w-4 text-white/60" />
                        <Input placeholder="Buscar por nombre o DNI..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="max-w-sm bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-white/60 text-center py-8">Cargando...</p>
                    ) : cards.length === 0 ? (
                        <p className="text-white/60 text-center py-8">No se encontraron tarjetas.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm min-w-[600px]">
                                    <thead>
                                        <tr className="border-b border-[#3a3a3f]">
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Alumno</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Plan</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Inicio</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Fin</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Clases</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Pago</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Estado</th>
                                            <th className="text-right py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cards.map(card => (
                                            <tr key={card.id} className="border-b border-[#3a3a3f] last:border-0 hover:bg-white/5">
                                                <td className="py-3 px-2 font-medium text-white">
                                                    {card.customer?.first_name} {card.customer?.last_name}
                                                    <div className="text-xs text-white/60 font-mono">{card.customer?.dni}</div>
                                                </td>
                                                <td className="py-3 px-2 text-white/60 text-sm">{card.plan?.name || '-'}</td>
                                                <td className="py-3 px-2 text-white/60">{formatDate(card.start_date)}</td>
                                                <td className="py-3 px-2 text-white/60">{formatDate(card.end_date)}</td>
                                                <td className="py-3 px-2 text-white">
                                                    <span className="font-semibold">{card.used_classes}</span>
                                                    <span className="text-white/60">/{card.total_classes}</span>
                                                </td>
                                                <td className="py-3 px-2 text-white/60 text-sm capitalize">{card.payment_method || '-'}</td>
                                                <td className="py-3 px-2">{statusBadge(card)}</td>
                                                <td className="py-3 px-2 text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <Button variant="ghost" size="icon" onClick={() => openEdit(card)} className="text-white hover:bg-white/5"><Pencil className="h-4 w-4" /></Button>
                                                        <Button variant="ghost" size="icon" onClick={() => handleDelete(card.id)} className="text-white hover:bg-white/5"><Trash2 className="h-4 w-4" /></Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {meta.last_page > 1 && (
                                <div className="flex justify-center gap-2 mt-4">
                                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-[#3a3a3f] text-white hover:bg-white/5">Anterior</Button>
                                    <span className="flex items-center text-sm text-white/60">Página {meta.current_page} de {meta.last_page}</span>
                                    <Button variant="outline" size="sm" disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)} className="border-[#3a3a3f] text-white hover:bg-white/5">Siguiente</Button>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-white uppercase tracking-wide">{editing ? 'Editar Tarjeta' : 'Nueva Tarjeta'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                        {error && <div className="bg-white/5 text-white text-sm p-3 rounded-md">{error}</div>}
                        {editing ? (
                            <>
                                <div className="p-3 bg-[#0a0a0a] border border-[#3a3a3f] rounded-md text-sm text-white">
                                    <strong>{editing.customer?.first_name} {editing.customer?.last_name}</strong> — DNI: {editing.customer?.dni}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">Total de clases</Label>
                                        <Input type="number" min="1" value={editForm.total_classes} onChange={e => setEditForm({ ...editForm, total_classes: parseInt(e.target.value) })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">Clases usadas</Label>
                                        <Input type="number" min="0" value={editForm.used_classes} onChange={e => setEditForm({ ...editForm, used_classes: parseInt(e.target.value) })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Estado</Label>
                                    <select className="flex h-9 w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-1 text-sm text-white" value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                                        <option value="active" className="bg-[#0a0a0a] text-white">Activa</option>
                                        <option value="expired" className="bg-[#0a0a0a] text-white">Vencida</option>
                                        <option value="cancelled" className="bg-[#0a0a0a] text-white">Cancelada</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Fecha de fin</Label>
                                    <Input type="date" value={editForm.end_date} onChange={e => setEditForm({ ...editForm, end_date: e.target.value })} className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="space-y-2">
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Alumno</Label>
                                    <SearchSelect
                                        options={customers}
                                        value={form.customer_id}
                                        onChange={id => setForm({ ...form, customer_id: id })}
                                        placeholder="Seleccionar alumno..."
                                        searchPlaceholder="Buscar por nombre o DNI..."
                                        getLabel={c => `${c.first_name} ${c.last_name} — ${c.dni}`}
                                        getSearchLabel={c => `${c.first_name} ${c.last_name} ${c.dni}`}
                                    />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">Fecha de inicio</Label>
                                        <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">Total de clases</Label>
                                        <Input type="number" min="1" value={form.total_classes} onChange={e => setForm({ ...form, total_classes: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">Plan</Label>
                                        <SearchSelect
                                            options={plans.filter(p => p.is_active !== false)}
                                            value={form.gym_plan_id}
                                            onChange={id => {
                                                const plan = plans.find(p => p.id === id);
                                                setForm({
                                                    ...form,
                                                    gym_plan_id: id,
                                                    total_classes: plan ? plan.total_classes : form.total_classes,
                                                });
                                            }}
                                            placeholder="Sin plan..."
                                            searchPlaceholder="Buscar plan..."
                                            getLabel={p => `${p.name} — ${p.total_classes} clases` + (p.price ? ` — $${p.price}` : '')}
                                            getSearchLabel={p => `${p.name} ${p.total_classes} clases ${p.price || ''}`}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">Método de pago</Label>
                                        <SearchSelect
                                            options={paymentMethods}
                                            value={form.payment_method}
                                            onChange={id => setForm({ ...form, payment_method: id })}
                                            placeholder="Seleccionar método..."
                                            searchPlaceholder="Buscar método..."
                                            getLabel={pm => pm.name}
                                            getSearchLabel={pm => pm.name}
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-white/60">Si seleccionás un plan, la duración y las clases se completan automáticamente según el plan.</p>
                            </>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5">Cancelar</Button>
                            <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
